/**
 * ReviewCommentsView — Top-level container for the Review Comments sub-tab.
 * Fetches review comments for the active BookletEdition, manages filter state,
 * and orchestrates FilterBar, left panel (Reviewer or Submission), and CommentsPanel.
 * Responsive: stacks vertically on mobile with a dropdown selector.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiClient } from '../../../api/client';
import type { ReviewCommentViewItem, SectionCategory } from './types';
import { getVisibleReviewers, getFilteredComments } from './utils';
import { FilterBar } from './FilterBar';
import { ReviewerPanel } from './ReviewerPanel';
import { SubmissionPanel } from './SubmissionPanel';
import { CommentsPanel } from './CommentsPanel';
import {
  BG_PAGE,
  TEXT_DARK,
  TEXT_MUTED,
  PRIMARY,
  BORDER_LIGHT,
} from '../../../components/legacy-ball/table-management/designTokens';

type LeftPanelMode = 'reviewer' | 'submission';

interface EditionInfo {
  id: string;
  name: string;
}

interface SubmissionSummary {
  id: string;
  title: string;
  sectionCategory: SectionCategory;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    backgroundColor: BG_PAGE,
    padding: '16px 24px',
    minHeight: '400px',
  },
  containerMobile: {
    width: '100%',
    backgroundColor: BG_PAGE,
    padding: '12px 12px',
    minHeight: '300px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  modeToggle: {
    display: 'flex',
    gap: '0',
    borderRadius: '6px',
    overflow: 'hidden',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  modeButton: {
    padding: '5px 12px',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#f3f4f6',
    color: TEXT_DARK,
    transition: 'background-color 0.15s, color 0.15s',
  },
  modeButtonActive: {
    padding: '5px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    transition: 'background-color 0.15s, color 0.15s',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    gap: '16px',
  },
  errorMessage: {
    color: TEXT_DARK,
    fontSize: '0.95rem',
    textAlign: 'center',
  },
  retryButton: {
    padding: '8px 20px',
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: '6px',
    border: `1px solid ${PRIMARY}`,
    backgroundColor: PRIMARY,
    color: '#ffffff',
    cursor: 'pointer',
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
  },
  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  modeLabel: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginRight: '8px',
  },
  mobileSelect: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: TEXT_DARK,
    cursor: 'pointer',
    appearance: 'auto' as React.CSSProperties['appearance'],
  },
  mobileSelectRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '12px',
  },
};

export function ReviewCommentsView(): JSX.Element {
  const isMobile = useIsMobile();
  const [comments, setComments] = useState<ReviewCommentViewItem[]>([]);
  const [allSubmissions, setAllSubmissions] = useState<SubmissionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [activeCategory, setActiveCategory] = useState<SectionCategory | null>(null);
  const [selectedReviewer, setSelectedReviewer] = useState<string | 'all'>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<string | 'all'>('all');
  const [leftPanelMode, setLeftPanelMode] = useState<LeftPanelMode>('reviewer');

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch the active edition first
      const edition = await apiClient<EditionInfo>('/weekend-booklet/editions/active').catch(() => null);

      if (!edition) {
        setComments([]);
        setAllSubmissions([]);
        setLoading(false);
        return;
      }

      // Fetch comments and all submissions in parallel
      const [commentsData, submissionsData] = await Promise.all([
        apiClient<ReviewCommentViewItem[]>('/weekend-booklet/review-comments'),
        apiClient<SubmissionSummary[]>(`/weekend-booklet/submissions?editionId=${edition.id}`),
      ]);

      setComments(commentsData);
      setAllSubmissions(submissionsData);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Unable to load review comments. Please check your connection and try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Handle category change with reviewer/submission reset logic
  const handleCategoryChange = useCallback(
    (category: SectionCategory | null) => {
      setActiveCategory(category);

      if (category === null) return;

      if (selectedReviewer !== 'all') {
        const hasCommentsInCategory = comments.some(
          (c) =>
            c.sectionCategory === category &&
            c.commenterName.toLowerCase() === selectedReviewer.toLowerCase(),
        );
        if (!hasCommentsInCategory) {
          setSelectedReviewer('all');
        }
      }

      if (selectedSubmission !== 'all') {
        const submissionInCategory = allSubmissions.some(
          (s) => s.id === selectedSubmission && s.sectionCategory === category,
        );
        if (!submissionInCategory) {
          setSelectedSubmission('all');
        }
      }
    },
    [comments, allSubmissions, selectedReviewer, selectedSubmission],
  );

  const handleReviewerSelect = useCallback((reviewer: string | 'all') => {
    setSelectedReviewer(reviewer);
  }, []);

  const handleSubmissionSelect = useCallback((submissionId: string | 'all') => {
    setSelectedSubmission(submissionId);
  }, []);

  const handleModeChange = useCallback((mode: LeftPanelMode) => {
    setLeftPanelMode(mode);
    setSelectedReviewer('all');
    setSelectedSubmission('all');
  }, []);

  // Derive visible reviewers
  const visibleReviewers = useMemo(
    () => getVisibleReviewers(comments, activeCategory),
    [comments, activeCategory],
  );

  // Derive visible submissions from ALL submissions (not just ones with comments)
  const visibleSubmissions = useMemo(() => {
    const filtered = activeCategory
      ? allSubmissions.filter((s) => s.sectionCategory === activeCategory)
      : allSubmissions;

    return filtered
      .map((s) => ({ id: s.id, title: s.title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [allSubmissions, activeCategory]);

  // Set of submission IDs that have at least one review comment
  const submissionsWithComments = useMemo(() => {
    const ids = new Set<string>();
    for (const c of comments) {
      ids.add(c.submissionId);
    }
    return ids;
  }, [comments]);

  // Derive filtered comments based on active mode
  const filteredComments = useMemo(() => {
    if (leftPanelMode === 'reviewer') {
      return getFilteredComments(comments, activeCategory, selectedReviewer);
    }
    let result = comments;
    if (activeCategory) {
      result = result.filter((c) => c.sectionCategory === activeCategory);
    }
    if (selectedSubmission !== 'all') {
      result = result.filter((c) => c.submissionId === selectedSubmission);
    }
    return result.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [comments, activeCategory, selectedReviewer, selectedSubmission, leftPanelMode]);

  const emptyMessage = useMemo(() => {
    if (comments.length === 0) {
      return 'No review comments have been submitted for this edition yet.';
    }
    return 'No comments match the current selection.';
  }, [comments.length]);

  // Error state
  if (error) {
    return (
      <div style={isMobile ? styles.containerMobile : styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorMessage}>{error}</div>
          <button style={styles.retryButton} onClick={fetchComments}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Mobile layout: stacked with dropdown selectors
  if (isMobile) {
    return (
      <div style={styles.containerMobile}>
        <FilterBar activeCategory={activeCategory} onCategoryChange={handleCategoryChange} />
        <div style={styles.mobileSelectRow}>
          <div style={styles.modeToggle}>
            <button
              style={leftPanelMode === 'reviewer' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => handleModeChange('reviewer')}
            >
              Reviewer
            </button>
            <button
              style={leftPanelMode === 'submission' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => handleModeChange('submission')}
            >
              Submission
            </button>
          </div>
        </div>
        <div style={{ marginBottom: '12px' }}>
          {leftPanelMode === 'reviewer' ? (
            <select
              style={styles.mobileSelect}
              value={selectedReviewer}
              onChange={(e) => handleReviewerSelect(e.target.value as string | 'all')}
              aria-label="Select reviewer"
            >
              <option value="all">All Reviewers</option>
              {visibleReviewers.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          ) : (
            <select
              style={styles.mobileSelect}
              value={selectedSubmission}
              onChange={(e) => handleSubmissionSelect(e.target.value as string | 'all')}
              aria-label="Select submission"
            >
              <option value="all">All Submissions ({visibleSubmissions.length})</option>
              {visibleSubmissions.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          )}
        </div>
        <CommentsPanel
          comments={filteredComments}
          loading={loading}
          emptyMessage={emptyMessage}
          selectedSubmissionId={leftPanelMode === 'submission' ? selectedSubmission : undefined}
        />
      </div>
    );
  }

  // Desktop layout: side-by-side
  return (
    <div style={styles.container}>
      <div style={styles.topRow}>
        <FilterBar activeCategory={activeCategory} onCategoryChange={handleCategoryChange} />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={styles.modeLabel}>View by:</span>
          <div style={styles.modeToggle}>
            <button
              style={leftPanelMode === 'reviewer' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => handleModeChange('reviewer')}
            >
              Reviewer
            </button>
            <button
              style={leftPanelMode === 'submission' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => handleModeChange('submission')}
            >
              Submission
            </button>
          </div>
        </div>
      </div>
      <div style={styles.contentRow}>
        {leftPanelMode === 'reviewer' ? (
          <ReviewerPanel
            reviewers={visibleReviewers}
            selectedReviewer={selectedReviewer}
            onReviewerSelect={handleReviewerSelect}
            loading={loading}
          />
        ) : (
          <SubmissionPanel
            submissions={visibleSubmissions}
            selectedSubmission={selectedSubmission}
            onSubmissionSelect={handleSubmissionSelect}
            loading={loading}
            submissionsWithComments={submissionsWithComments}
          />
        )}
        <CommentsPanel
          comments={filteredComments}
          loading={loading}
          emptyMessage={emptyMessage}
          selectedSubmissionId={leftPanelMode === 'submission' ? selectedSubmission : undefined}
        />
      </div>
    </div>
  );
}
